// create-github-remote-with-recovery-menu.ts
//
// v3: Wrap tạo GitHub remote (repo rỗng cho "dự án mới", HOẶC từ folder có sẵn)
// với recovery menu khi fail: tên trùng / sai quyền / sai account / network.
// Thay vì throw thô + process.exit(1) → menu: đổi tên / switch account / retry / abort.
//
// Dùng chung cho 2 nhánh add-repo (new + folder-create-remote) → DRY.

import { createEmptyGithubRepoAndGetUrl, RepoCreateError } from "./create-empty-github-repo.js";
import { isNonInteractive, MissingFlagError } from "./non-interactive-context.js";
import { inputOrFlag, selectOrFlag } from "./prompt-or-flag.js";
import { UserAbortedRecoveryError } from "./prompt-recovery-action-on-failure.js";
import { log } from "./terminal-logger.js";
import { triggerGhCliAuthLogin } from "./trigger-gh-cli-auth-login.js";

// Giải thích lỗi theo reason + gợi ý check account.
function explainCreateFailure(reason: string): void {
  switch (reason) {
    case "name-exists":
      log.warn("⛔ Repo tên này đã tồn tại trên GitHub.");
      log.dim("  → Đổi tên khác, hoặc dùng account/org khác.");
      break;
    case "no-permission":
      log.warn("⛔ Không có quyền tạo repo (sai account / org).");
      log.dim("  → Kiểm tra: đang login đúng GitHub account chưa? (gh auth status)");
      break;
    case "network":
      log.warn("⛔ Lỗi mạng khi tạo repo.");
      break;
    default:
      log.warn("⛔ Tạo repo thất bại (lỗi không xác định).");
      log.dim("  → Kiểm tra gh auth status + thử lại.");
  }
}

// Menu recovery. Offer switch-account khi lỗi quyền; đổi-tên khi trùng tên.
async function promptCreateRecovery(
  reason: string,
): Promise<"retry" | "rename" | "switch-account" | "abort"> {
  const choices: Array<{ name: string; value: string }> = [];
  if (reason === "name-exists") {
    choices.push({ name: "Đổi tên repo khác", value: "rename" });
  }
  if (reason === "no-permission" || reason === "unknown") {
    choices.push({
      name: "Switch GitHub account (gh auth login — mở browser)",
      value: "switch-account",
    });
  }
  choices.push({ name: "Thử lại (retry)", value: "retry" });
  choices.push({ name: "Hủy", value: "abort" });

  // Non-interactive: không ai bấm menu. `--on-remote-create-fail` mặc định
  // `abort` ⇒ KHÔNG tạo remote nửa vời, không đổi git remote của folder.
  if (isNonInteractive()) {
    throw new MissingFlagError({
      missingFlag: "--on-remote-create-fail",
      command: "add repo",
      hint: "--on-remote-create-fail rename|skip (mặc định abort — không tạo remote nửa vời)",
    });
  }

  return (await selectOrFlag(undefined, REMOTE_CREATE_FAIL_FLAG_SPEC, {
    message: "Cách xử lý?",
    choices,
  })) as "retry" | "rename" | "switch-account" | "abort";
}

// Tạo repo GitHub rỗng (cho "dự án mới") với recovery loop. Trả SSH URL.
// Throw UserAbortedRecoveryError nếu user hủy.
const REMOTE_CREATE_FAIL_FLAG_SPEC = {
  flag: "--on-remote-create-fail",
  command: "add repo",
  hint: "--on-remote-create-fail abort|rename|skip",
};
const RENAME_FLAG_SPEC = {
  flag: "--new-repo-name",
  command: "add repo",
  hint: "--new-repo-name ten-khac",
};

export async function createEmptyRepoWithRecovery(
  initialName: string,
  visibility: "private" | "public",
): Promise<string> {
  let name = initialName;

  while (true) {
    try {
      return await createEmptyGithubRepoAndGetUrl(name, visibility);
    } catch (err) {
      const reason = err instanceof RepoCreateError ? err.reason : "unknown";
      const next = await runRecovery(reason, name);
      if (next.action === "retry") continue;
      name = next.newName;
    }
  }
}

// Tạo repo GitHub TỪ FOLDER (push code lên) với recovery loop. Trả SSH URL.
// createFromFolder: callback gọi createGithubRemoteFromFolder, throw nếu fail.
export async function createRemoteFromFolderWithRecovery(
  initialName: string,
  createFromFolder: (name: string) => string,
): Promise<string> {
  let name = initialName;

  while (true) {
    try {
      return createFromFolder(name);
    } catch (err) {
      // RepoAlreadyExistsError → name-exists; còn lại → unknown (có thể sai account).
      const reason =
        err instanceof Error && err.name === "RepoAlreadyExistsError" ? "name-exists" : "unknown";
      const next = await runRecovery(reason, name);
      if (next.action === "retry") continue;
      name = next.newName;
    }
  }
}

// Recovery loop dùng chung: giải thích lỗi → menu → trả {action, newName}.
// action="retry" giữ name; action="rename" trả name mới. abort/switch xử lý nội bộ.
async function runRecovery(
  reason: string,
  currentName: string,
): Promise<{ action: "retry"; newName: string } | { action: "rename"; newName: string }> {
  explainCreateFailure(reason);
  const choice = await promptCreateRecovery(reason);

  if (choice === "abort") {
    throw new UserAbortedRecoveryError("User hủy tại bước tạo repo GitHub.");
  }
  if (choice === "switch-account") {
    triggerGhCliAuthLogin();
    return { action: "retry", newName: currentName };
  }
  if (choice === "rename") {
    const newName = (
      await inputOrFlag(undefined, RENAME_FLAG_SPEC, {
        message: "Tên repo mới:",
        default: currentName,
        validate: (v) => (v.trim().length > 0 ? true : "Tên bắt buộc"),
      })
    ).trim();
    return { action: "rename", newName };
  }
  return { action: "retry", newName: currentName };
}
